import type { Pot, SeatPlayer } from '@/types';

export class PotManager {
  private pots: Pot[] = [];
  private currentPotId = 0;

  constructor(players: SeatPlayer[]) {
    // 初始化主池，所有玩家都有资格
    this.pots.push({
      id: this.currentPotId++,
      amount: 0,
      eligiblePlayers: players.filter(p => p.status !== 'eliminated').map(p => p.id),
    });
  }

  // 处理玩家下注
  handleBet(player: SeatPlayer, amount: number): void {
    if (amount <= 0) return;

    // 找到玩家有资格参与的所有底池
    const eligiblePots = this.pots.filter(pot => 
      pot.eligiblePlayers.includes(player.id)
    );

    // 将下注分配到各个底池
    for (const pot of eligiblePots) {
      pot.amount += amount;
    }
  }

  // 创建边池（当玩家 all-in 时）
  createSidePots(players: SeatPlayer[]): void {
    // 按本回合总下注排序
    const sortedPlayers = [...players]
      .filter(p => p.status !== 'folded' && p.status !== 'eliminated')
      .sort((a, b) => a.totalBetThisRound - b.totalBetThisRound);

    if (sortedPlayers.length <= 1) return;

    // 计算需要创建的边池
    let previousBet = 0;
    const processedPlayers = new Set<string>();

    for (const player of sortedPlayers) {
      const currentBet = player.totalBetThisRound;
      
      if (currentBet > previousBet) {
        const diff = currentBet - previousBet;
        const eligiblePlayers = sortedPlayers
          .filter(p => p.totalBetThisRound >= currentBet && !processedPlayers.has(p.id))
          .map(p => p.id);

        if (eligiblePlayers.length > 0) {
          // 检查是否已存在相同的边池
          const existingPot = this.pots.find(p => 
            p.eligiblePlayers.length === eligiblePlayers.length &&
            p.eligiblePlayers.every(id => eligiblePlayers.includes(id))
          );

          if (existingPot) {
            existingPot.amount += diff * eligiblePlayers.length;
          } else {
            this.pots.push({
              id: this.currentPotId++,
              amount: diff * eligiblePlayers.length,
              eligiblePlayers,
            });
          }
        }

        previousBet = currentBet;
      }

      processedPlayers.add(player.id);
    }
  }

  // 获取所有底池
  getPots(): Pot[] {
    return this.pots;
  }

  // 获取主池金额
  getMainPotAmount(): number {
    return this.pots[0]?.amount || 0;
  }

  // 获取总底池金额
  getTotalPotAmount(): number {
    return this.pots.reduce((sum, pot) => sum + pot.amount, 0);
  }

  // 结算底池
  settlePots(winners: Map<number, string[]>): Map<string, number> {
    const winnings = new Map<string, number>();

    for (const [potId, potWinners] of winners) {
      const pot = this.pots.find(p => p.id === potId);
      if (!pot || potWinners.length === 0) continue;

      const share = Math.floor(pot.amount / potWinners.length);
      const remainder = pot.amount % potWinners.length;

      potWinners.forEach((winnerId, index) => {
        const currentWin = winnings.get(winnerId) || 0;
        // 第一个赢家获得余数（如果有）
        const extra = index === 0 ? remainder : 0;
        winnings.set(winnerId, currentWin + share + extra);
      });
    }

    return winnings;
  }

  // 重置底池（新一轮）
  resetPots(players: SeatPlayer[]): void {
    this.pots = [{
      id: 0,
      amount: 0,
      eligiblePlayers: players.filter(p => p.status !== 'eliminated').map(p => p.id),
    }];
    this.currentPotId = 1;
  }

  // 获取玩家有资格参与的底池
  getPlayerPots(playerId: string): Pot[] {
    return this.pots.filter(pot => pot.eligiblePlayers.includes(playerId));
  }

  // 计算玩家需要跟注的金额
  getCallAmount(player: SeatPlayer, currentBet: number): number {
    return Math.min(currentBet - player.currentBet, player.chips);
  }

  // 计算最小加注额
  getMinRaise(currentBet: number, lastRaise: number, bigBlind: number): number {
    const minRaiseAmount = Math.max(currentBet + lastRaise, currentBet + bigBlind);
    return minRaiseAmount;
  }
}
